import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Search, Heart, Calendar, Menu } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { getCollectedVerses } from '../utils/localStorage';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { SideMenu } from './SideMenu';

export const CollectionPage = () => {
  const navigate = useNavigate();
  const [verses, setVerses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState(new Set());
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const collected = getCollectedVerses();
    setVerses(collected);
  }, []);

  const filteredVerses = verses.filter(
    (verse) =>
      verse.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      verse.reference.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedVerses = filteredVerses.reduce((acc, verse) => {
    const bookName = verse.reference.split(' ')[0];
    if (!acc[bookName]) acc[bookName] = [];
    acc[bookName].push(verse);
    return acc;
  }, {});

  const toggleFavorite = (reference) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(reference)) {
        newFavorites.delete(reference);
      } else {
        newFavorites.add(reference);
      }
      return newFavorites;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 pb-24">
      {/* Side Menu */}
      <SideMenu open={menuOpen} onOpenChange={setMenuOpen} />

      {/* Header */}
      <header className="sticky top-0 bg-background/80 glass border-b border-border backdrop-blur-lg z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => setMenuOpen(true)}
              className="p-2 hover:bg-secondary rounded-full interactive"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Mi Colección
              </h1>
              <p className="text-sm text-muted-foreground">
                {verses.length} versículos en progreso
              </p>
            </div>
          </div>

          <div className="bg-primary/10 p-2 rounded-lg border border-primary/20 mb-4">
            <p className="text-[10px] text-primary font-medium text-center">
              💡 Los versículos se guardan aquí hasta que completes el libro al 100%.
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar en tu colección..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-12 bg-card border-2 rounded-2xl"
            />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {verses.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold mb-2">No hay versículos aún</h3>
            <p className="text-muted-foreground mb-6">
              Juega Tetris para revelar y coleccionar versículos bíblicos
            </p>
            <button
              onClick={() => navigate('/select-book')}
              className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold interactive"
            >
              Comenzar a jugar
            </button>
          </div>
        )}

        <div className="space-y-8">
          {Object.entries(groupedVerses).map(([bookName, bookVerses]) => (
            <div key={bookName} className="space-y-4">
              <div className="flex items-center gap-2 border-b border-border pb-2">
                <BookOpen className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold">{bookName}</h2>
                <Badge variant="outline" className="ml-auto">
                  {bookVerses.length} versículos
                </Badge>
              </div>

              <div className="grid gap-4">
                {bookVerses.map((verse, index) => (
                  <Card
                    key={`${verse.reference}-${index}`}
                    className="p-5 interactive hover:shadow-lg border-l-4 border-primary bg-card/50 backdrop-blur-sm"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <Badge variant="secondary" className="text-xs">
                        {verse.reference}
                      </Badge>
                      <button
                        onClick={() => toggleFavorite(verse.reference)}
                        className="p-1 hover:bg-secondary rounded-full transition-colors"
                      >
                        <Heart
                          className={`w-5 h-5 ${favorites.has(verse.reference)
                            ? 'fill-red-500 text-red-500'
                            : 'text-muted-foreground'
                            }`}
                        />
                      </button>
                    </div>
                    <p className="text-base leading-relaxed mb-3">{verse.text}</p>
                    {verse.collectedAt && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>
                          Revelado el{' '}
                          {format(new Date(verse.collectedAt), "d 'de' MMMM, yyyy", { locale: es })}
                        </span>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredVerses.length === 0 && verses.length > 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No se encontraron versículos con "{searchTerm}"</p>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-2xl">
        <div className="max-w-2xl mx-auto px-4 py-4 grid grid-cols-4 gap-2">
          <button
            onClick={() => navigate('/')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary interactive"
          >
            <div className="w-5 h-5 bg-muted-foreground rounded"></div>
            <span className="text-xs text-muted-foreground">Inicio</span>
          </button>
          <button
            onClick={() => navigate('/collection')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg bg-primary text-primary-foreground"
          >
            <BookOpen className="w-5 h-5" />
            <span className="text-xs font-semibold">Colección</span>
          </button>
          <button
            onClick={() => navigate('/ranking')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary interactive"
          >
            <svg className="w-5 h-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="20" x2="12" y2="10" />
              <line x1="18" y1="20" x2="18" y2="4" />
              <line x1="6" y1="20" x2="6" y2="16" />
            </svg>
            <span className="text-xs text-muted-foreground">Ranking</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary interactive"
          >
            <svg className="w-5 h-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span className="text-xs text-muted-foreground">Perfil</span>
          </button>
        </div>
      </nav>
    </div>
  );
};